import { useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'

import { ValidateTaxIdDocument } from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

atlas`#graphql
  query ValidateTaxId($country: String!, $taxId: String!) {
    validateTaxId(country: $country, taxId: $taxId) {
      isValid
      message
    }
  }
`

export type ValidateTaxIdStatus =
  | { status: 'valid' }
  | { status: 'invalid'; message: string }
  | { status: 'error'; message: string }

export function useValidateTaxId() {
  const [execute, { loading }] = useLazyQuery(ValidateTaxIdDocument, {
    client: atlasClient,
    fetchPolicy: 'network-only',
  })

  const validateTaxId = useCallback(
    async (country: string, taxId: string): Promise<ValidateTaxIdStatus> => {
      try {
        const { data, error } = await execute({
          variables: {
            country: country.trim().toLowerCase(),
            taxId: taxId.trim(),
          },
        })

        if (error) {
          return {
            status: 'error',
            message: error.message || 'Could not validate Tax ID.',
          }
        }

        const payload = data?.validateTaxId
        if (payload == null) {
          return {
            status: 'error',
            message: 'Could not validate Tax ID.',
          }
        }

        if (payload.isValid) {
          return { status: 'valid' }
        }

        const msg =
          typeof payload.message === 'string' && payload.message.trim()
            ? payload.message.trim()
            : 'Tax ID could not be verified.'

        return { status: 'invalid', message: msg }
      } catch {
        return {
          status: 'error',
          message: 'Could not validate Tax ID.',
        }
      }
    },
    [execute],
  )

  return { validateTaxId, validating: loading }
}
